import type { Evenement } from '@mtc/shared';
import { useNavigate } from 'react-router-dom';
import { ImgPlaceholder } from '../components/ImgPlaceholder';
import { StateMessage } from '../components/StateMessage';
import { useEvenements } from '../data/queries';
import { useSEO } from '../hooks/useSEO';

const CATEGORY_LABEL: Record<Evenement['category'], string> = {
  congres: 'Congrès',
  symposium: 'Symposium',
  workshop: 'Workshop',
  webinaire: 'Webinaire',
  autre: 'Événement',
};

export function EvenementsList() {
  const navigate = useNavigate();
  const eventsQ = useEvenements();

  useSEO({
    title: 'Événements scientifiques — MTC',
    description:
      "Congrès, symposia et workshops dentaires à suivre — sélection MTC pour la communauté.",
  });

  if (eventsQ.isLoading) return <StateMessage variant="loading" />;
  if (eventsQ.error) return <StateMessage variant="error" message={String(eventsQ.error)} />;

  const events = eventsQ.data ?? [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const upcoming = events.filter((e) => new Date(e.endDate) >= today);
  const past = events.filter((e) => new Date(e.endDate) < today);

  return (
    <div className="page-evenements" style={{ padding: '64px 32px 96px', maxWidth: 1280, margin: '0 auto' }}>
      <header style={{ marginBottom: 48 }}>
        <span className="mtc-eyebrow">Communauté</span>
        <h1 style={{ fontFamily: "var(--mtc-font-display)", fontSize: 'clamp(40px, 5vw, 72px)', fontWeight: 400, lineHeight: 1.0, letterSpacing: '-0.03em', margin: '12px 0 16px' }}>
          Événements scientifiques
        </h1>
        <p style={{ fontSize: 'clamp(15px, 1.2vw, 17px)', lineHeight: 1.5, color: 'rgba(15,25,22,0.65)', maxWidth: 640, margin: 0 }}>
          Sélection de congrès, symposia et workshops dentaires à suivre au Maroc et à
          l'international. Restez informé·e des temps forts qui comptent pour la profession.
        </p>
      </header>

      {upcoming.length === 0 && past.length === 0 ? (
        <StateMessage
          variant="empty"
          message="Aucun événement programmé pour le moment. Revenez bientôt."
        />
      ) : (
        <>
          {upcoming.length > 0 && (
            <Section title="À venir" events={upcoming} navigate={navigate} />
          )}
          {past.length > 0 && (
            <Section title="Passés" events={past} navigate={navigate} muted />
          )}
        </>
      )}
    </div>
  );
}

function Section({
  title,
  events,
  navigate,
  muted = false,
}: {
  title: string;
  events: Evenement[];
  navigate: ReturnType<typeof useNavigate>;
  muted?: boolean;
}) {
  return (
    <section style={{ marginBottom: 64 }}>
      <h2
        style={{
          fontFamily: 'var(--mtc-font-mono)',
          fontSize: 11,
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          color: 'rgba(15,25,22,0.55)',
          marginBottom: 20,
        }}
      >
        {title} · {events.length}
      </h2>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: 24,
        }}
      >
        {events.map((e) => (
          <EventCard key={e.id} event={e} onClick={() => navigate(`/evenements/${e.id}`)} muted={muted} />
        ))}
      </div>
    </section>
  );
}

function EventCard({
  event,
  onClick,
  muted,
}: {
  event: Evenement;
  onClick: () => void;
  muted?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        all: 'unset',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        background: 'white',
        border: '0.5px solid rgba(15,25,22,0.1)',
        borderRadius: 12,
        overflow: 'hidden',
        transition: 'transform 0.2s, box-shadow 0.2s',
        opacity: muted ? 0.7 : 1,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 12px 32px rgba(15,25,22,0.08)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = '';
        e.currentTarget.style.boxShadow = '';
      }}
    >
      <ImgPlaceholder
        label={event.title}
        ratio="3/2"
        tone="vert"
        src={event.image}
        alt={event.title}
        fit="cover"
      />
      <div style={{ padding: '18px 20px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 11,
            fontFamily: 'var(--mtc-font-mono)',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'rgba(15,25,22,0.55)',
          }}
        >
          <span style={{ color: 'var(--mtc-primary, #0B5D3B)' }}>
            {CATEGORY_LABEL[event.category]}
          </span>
          <span>·</span>
          <span>{formatDateRange(event.startDate, event.endDate)}</span>
        </div>
        <h3
          style={{
            fontFamily: 'var(--mtc-font-display)',
            fontSize: 22,
            fontWeight: 400,
            lineHeight: 1.15,
            margin: 0,
            color: '#0F1916',
          }}
        >
          {event.title}
        </h3>
        <p style={{ fontSize: 14, lineHeight: 1.5, color: 'rgba(15,25,22,0.65)', margin: 0 }}>
          {event.shortDescription}
        </p>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: 8,
            fontSize: 12,
            color: 'rgba(15,25,22,0.55)',
          }}
        >
          <span>
            📍 {event.city}
            {event.country && event.country !== 'Maroc' ? `, ${event.country}` : ''}
          </span>
          <span style={{ color: 'var(--mtc-primary, #0B5D3B)', fontWeight: 500 }}>
            Détails →
          </span>
        </div>
      </div>
    </button>
  );
}

function formatDateRange(startIso: string, endIso: string): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (Number.isNaN(start.getTime())) return '';
  const fmt = (d: Date) =>
    d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  if (Number.isNaN(end.getTime()) || start.toDateString() === end.toDateString()) {
    return fmt(start);
  }
  // Same month: "12–14 juin 2026"
  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return `${start.getDate()}–${end.getDate()} ${end.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })}`;
  }
  return `${fmt(start)} → ${fmt(end)}`;
}
